import { useState, useEffect, useRef, useCallback } from 'react';
import { ZoomIn, ZoomOut, RotateCw, Download, X, Maximize2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Props {
  imageUrl: string | null;
  imageName: string;
  onClose: () => void;
}

/**
 * Lightbox moderno estilo WhatsApp Web / Photos do iOS.
 * - Backdrop preto translúcido (foco total na imagem).
 * - Scroll do mouse aplica zoom centralizado no cursor.
 * - Drag arrasta a imagem quando está zoomed (>100%).
 * - Double-click alterna entre fit (100%) e 200%.
 * - Click no backdrop fecha. ESC fecha.
 * - Toolbar flutuante semi-transparente no topo.
 *
 * Compartilhado por: WhatsApp Inbox (ChatWindow) e Anexos do Lead (AnexosList).
 * API estável: (imageUrl, imageName, onClose) — não mudar sem atualizar callers.
 */
export function ImagePreviewModal({ imageUrl, imageName, onClose }: Props) {
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const isOpen = !!imageUrl;
  const canPan = zoom > 100;

  // Reset estado a cada nova imagem
  useEffect(() => {
    if (imageUrl) {
      setZoom(100);
      setRotation(0);
      setOffset({ x: 0, y: 0 });
    }
  }, [imageUrl]);

  // ESC fecha
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === '+' || e.key === '=') setZoom((z) => Math.min(400, z + 25));
      else if (e.key === '-') setZoom((z) => Math.max(25, z - 25));
      else if (e.key === '0') {
        setZoom(100);
        setOffset({ x: 0, y: 0 });
      }
    };
    window.addEventListener('keydown', onKey);
    // Trava scroll do body enquanto modal aberto
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen, onClose]);

  const handleDownload = useCallback(async () => {
    if (!imageUrl) return;
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = imageName || 'imagem.jpg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      window.open(imageUrl, '_blank');
    }
  }, [imageUrl, imageName]);

  const handleZoomIn = () => setZoom((z) => Math.min(400, z + 25));
  const handleZoomOut = () =>
    setZoom((z) => {
      const next = Math.max(25, z - 25);
      if (next <= 100) setOffset({ x: 0, y: 0 }); // sem zoom, volta ao centro
      return next;
    });
  const handleRotate = () => setRotation((r) => (r + 90) % 360);
  const handleFit = () => {
    setZoom(100);
    setOffset({ x: 0, y: 0 });
  };

  // Wheel zoom centrado no cursor: ao dar scroll, calcula o ponto da imagem
  // sob o mouse e reposiciona offset pra manter aquele ponto fixo.
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY < 0 ? 12 : -12; // positivo = zoom in
    setZoom((prevZoom) => {
      const nextZoom = Math.min(400, Math.max(25, prevZoom + delta));
      if (nextZoom === prevZoom) return prevZoom;

      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        // Vetor do centro até o cursor
        const dx = e.clientX - cx;
        const dy = e.clientY - cy;
        const ratio = nextZoom / prevZoom;
        // Ajusta offset pra manter o ponto sob o cursor estável
        setOffset((prev) => ({
          x: dx - (dx - prev.x) * ratio,
          y: dy - (dy - prev.y) * ratio,
        }));
      }
      return nextZoom;
    });
  };

  // Drag pra pan quando zoomed
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!canPan) return;
    e.preventDefault();
    setIsDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !dragStart.current) return;
    setOffset({
      x: dragStart.current.ox + (e.clientX - dragStart.current.x),
      y: dragStart.current.oy + (e.clientY - dragStart.current.y),
    });
  };
  const handleMouseUp = () => {
    setIsDragging(false);
    dragStart.current = null;
  };

  // Double-click alterna fit ↔ 200%
  const handleDoubleClick = () => {
    if (zoom !== 100) {
      handleFit();
    } else {
      setZoom(200);
    }
  };

  // Click no backdrop (fora da imagem) fecha
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-black/95 backdrop-blur-sm animate-in fade-in duration-150"
      role="dialog"
      aria-modal="true"
      aria-label="Visualização de imagem"
    >
      {/* Toolbar flutuante no topo. No app instalado no iPhone a tela vai até
          embaixo do relógio: sem descer a barra, o X ficava sob a bateria e a
          foto não fechava (Douglas 10/10/2026). No computador o env() é 0. */}
      <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between gap-2 pb-3 pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] pt-[max(0.75rem,env(safe-area-inset-top))] bg-gradient-to-b from-black/70 to-transparent">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-medium text-white/90 truncate max-w-[40vw]">
            {imageName}
          </span>
          <span className="text-xs font-mono text-white/50 select-none tabular-nums">
            {zoom}%
          </span>
        </div>

        <div className="flex items-center gap-1">
          <ToolbarBtn onClick={handleZoomOut} disabled={zoom <= 25} title="Diminuir (-)">
            <ZoomOut className="w-4 h-4" />
          </ToolbarBtn>
          <ToolbarBtn onClick={handleZoomIn} disabled={zoom >= 400} title="Aumentar (+)">
            <ZoomIn className="w-4 h-4" />
          </ToolbarBtn>
          <ToolbarBtn onClick={handleFit} title="Ajustar à tela (0)">
            <Maximize2 className="w-4 h-4" />
          </ToolbarBtn>
          <ToolbarBtn onClick={handleRotate} title="Rotacionar">
            <RotateCw className="w-4 h-4" />
          </ToolbarBtn>
          <ToolbarBtn onClick={handleDownload} title="Baixar">
            <Download className="w-4 h-4" />
          </ToolbarBtn>
          <div className="w-px h-5 bg-card/20 mx-1" />
          <ToolbarBtn onClick={onClose} title="Fechar (ESC)">
            <X className="w-4 h-4" />
          </ToolbarBtn>
        </div>
      </div>

      {/* Área da imagem */}
      <div
        ref={containerRef}
        className={cn(
          'flex-1 flex items-center justify-center overflow-hidden select-none',
          canPan ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-zoom-in',
        )}
        onClick={handleBackdropClick}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onDoubleClick={handleDoubleClick}
      >
        <img
          ref={imgRef}
          src={imageUrl || ''}
          alt={imageName}
          draggable={false}
          className="max-w-[92vw] max-h-[92vh] object-contain shadow-2xl will-change-transform"
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom / 100}) rotate(${rotation}deg)`,
            transition: isDragging ? 'none' : 'transform 0.18s cubic-bezier(0.22, 1, 0.36, 1)',
            transformOrigin: 'center center',
          }}
          onClick={(e) => e.stopPropagation()}
        />
      </div>

      {/* Dica de atalhos no rodapé (sutil, some no hover) */}
      <div className="absolute bottom-[max(0.75rem,env(safe-area-inset-bottom))] left-1/2 -translate-x-1/2 text-[11px] text-white/40 select-none pointer-events-none">
        <span className="[@media(pointer:coarse)]:hidden">Scroll: zoom · Arrastar: mover · Duplo-clique: ajustar · ESC: fechar</span>
        <span className="hidden whitespace-nowrap [@media(pointer:coarse)]:inline">Toque fora da foto para fechar</span>
      </div>
    </div>
  );
}

function ToolbarBtn({
  children,
  onClick,
  disabled,
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  title: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        'inline-flex items-center justify-center h-9 w-9 rounded-full text-white/90',
        'hover:bg-card/15 active:bg-card/20 transition-colors',
        'disabled:opacity-40 disabled:cursor-not-allowed',
      )}
    >
      {children}
    </button>
  );
}
